<template>
  <div class="line-chart">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Chart from 'chart.js/auto';

const props = defineProps({
  chartData: {
    type: Object,
    default: () => ({ labels: [], datasets: [] })
  }
});

const canvasRef = ref(null);
let chart = null;

function rgbaFromHex(hexColor, alpha = 0.2) {
  const hex = String(hexColor || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function destroyChart() {
  if (chart) {
    chart.destroy();
    chart = null;
  }
}

async function renderChart() {
  await nextTick();
  if (!canvasRef.value) return;

  destroyChart();

  const labels = props.chartData?.labels || [];
  const datasets = (props.chartData?.datasets || []).map((item) => {
    const borderColor = item?.borderColor || '#2D9CDB';
    return {
      label: item?.label || '',
      data: item?.data || [],
      borderColor,
      backgroundColor: item?.backgroundColor || rgbaFromHex(borderColor, 0.2),
      fill: true,
      tension: 0.4,
      pointRadius: 3
    };
  });

  chart = new Chart(canvasRef.value.getContext('2d'), {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true }
      },
      scales: {
        y: {
          min: 0,
          max: 5,
          ticks: { stepSize: 1 }
        }
      }
    }
  });
}

watch(
  () => props.chartData,
  () => {
    renderChart();
  },
  { deep: true }
);

onMounted(() => {
  renderChart();
});

onBeforeUnmount(() => {
  destroyChart();
});
</script>

<style scoped>
.line-chart {
  width: 100%;
  height: 240px;
}

.line-chart canvas {
  width: 100%;
  height: 240px;
}
</style>
